// Clickable summary tiles that filter the table below them. G360_K402
//
// This is DeviceInventorySection's implementation moved somewhere it
// can be reused, not a new design. Its decisions are kept on purpose:
//
//   THE FIRST TILE IS ALWAYS "ALL", and it is the one that resets
//   everything - focus, search box, any page-local toggle. A set of
//   filters with no visible way back is how a person concludes their
//   data disappeared.
//
//   CLICKING THE ACTIVE TILE CLEARS IT. A filter you can only turn on
//   is a trap. The tile is a toggle and it looks like one, because it
//   carries aria-pressed and an outline in its own tone.
//
//   A FILTERED TABLE SAYS WHY. When a tile is active a banner appears
//   above the table stating what is being shown and offering one click
//   back. Without it, a table showing 3 of 300 rows looks like a bug.
//
//   THE TILE OWNS ITS PREDICATE. Each tile carries match(row), so the
//   number on the tile and the rows the tile reveals are computed from
//   the same function and cannot drift apart. A tile reading 14 that
//   filters to 9 rows destroys trust in every other number on screen.
//
//   A COUNT OF ZERO IS STILL A TILE. It is dimmed and not clickable,
//   but it stays, because a metric that vanishes when it reaches zero
//   is a metric nobody can confirm is zero.
//
// Nothing here has to be adopted all at once. A page can take the
// tiles without the banner, or compute its own counts and ignore
// match(). That is what makes retrofitting 61 pages survivable.

import { useState } from 'react';

/* Tone drives the outline of the active tile and the colour of a
   non-zero value. Names match the CSS variables already in tokens.css
   rather than introducing a second vocabulary for the same four
   colours. */
const TONE = {
  neutral: 'var(--g-primary)',
  warning: 'var(--g-warning)',
  danger: 'var(--g-danger)',
  success: 'var(--g-success)',
};
function toneVar(t) { return TONE[t] || TONE.neutral; }

/* ------------------------------------------------------------------ */
/* STATE                                                               */
/* ------------------------------------------------------------------ */

/* focus is the key of the active tile, or null for "all". tile(k)
   toggles, which is the whole reason this is a hook and not a plain
   useState in every page. */
export function useTileFocus(initial = null) {
  const [focus, setFocus] = useState(initial);
  function tile(k) { setFocus(function (f) { return f === k ? null : k; }); }
  function clear() { setFocus(null); }
  return { focus, setFocus, tile, clear };
}

/* ------------------------------------------------------------------ */
/* THE PREDICATE, USED FOR BOTH THE COUNT AND THE FILTER               */
/* ------------------------------------------------------------------ */

/* The count a tile displays. An explicit value wins - some counts come
   from the server and are not derivable from the rows on screen - but
   when match() is present the count comes from it, so the tile cannot
   advertise a number the filter will not produce. */
export function tileValue(t, rows) {
  if (!t) return 0;
  if (typeof t.value === 'number') return t.value;
  if (typeof t.match === 'function') return (rows || []).filter(t.match).length;
  return 0;
}

/* Apply the active tile to the rows. Callers chain this with their own
   search and toggles; order does not matter because every filter here
   is a pure predicate. */
export function tileFilter(rows, tiles, focus) {
  if (!focus) return rows || [];
  const t = (tiles || []).find(function (x) { return x.key === focus; });
  if (!t || typeof t.match !== 'function') return rows || [];
  return (rows || []).filter(t.match);
}

/* ------------------------------------------------------------------ */
/* THE TILES                                                           */
/* ------------------------------------------------------------------ */

/* tiles: [{ key, label, icon, value?, sub, tone, hint, match? }]
     key    null for the "all" tile, otherwise unique on the page
     label  what the number counts
     icon   a lucide component, already imported by the calling page
     sub    the small grey line under the number - say what the number
            means, not what it is called again
     hint   the sentence the banner shows while this tile is active
     match  row predicate; drives both the count and the filter

   rows are the rows BEFORE the tile filter, so counts do not collapse
   to the current view when a tile is active. */
export function FilterTiles({ tiles, rows, focus, onTile, onClear }) {
  const list = tiles || [];
  if (list.length === 0) return null;
  const cols = Math.min(Math.max(list.length, 2), 4);

  return (
    <div className={'row r' + cols} style={{ marginBottom: 14 }}>
      {list.map(function (t) {
        const active = focus === t.key;
        const n = tileValue(t, rows);
        const isAll = t.key === null || t.key === undefined;
        const dead = !isAll && n === 0;
        const Icon = t.icon;
        const col = toneVar(t.tone);

        function go() {
          if (dead) return;
          if (isAll) { if (onClear) onClear(); return; }
          if (onTile) onTile(t.key);
        }

        return (
          <div
            key={String(t.key)}
            className="card"
            role="button"
            tabIndex={dead ? -1 : 0}
            aria-pressed={active}
            aria-disabled={dead}
            onClick={go}
            onKeyDown={function (e) {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
            }}
            title={dead ? 'Nothing to show' : (t.hint || t.label)}
            style={{
              padding: '12px 14px',
              cursor: dead ? 'default' : 'pointer',
              opacity: dead ? 0.55 : 1,
              outline: active ? '2px solid ' + col : 'none',
            }}
          >
            <p className="metric-l">
              {Icon ? <Icon size={15} /> : null}{t.label}
            </p>
            <p
              className="metric-v"
              style={{
                fontSize: 24,
                color: (n > 0 && (t.tone === 'danger' || t.tone === 'warning')) ? col : undefined,
              }}
            >
              {n}
              {t.of != null ? (
                <span style={{ fontSize: 15, color: 'var(--g-text-2)' }}> / {t.of}</span>
              ) : null}
            </p>
            {t.sub ? <p className="metric-d nu">{t.sub}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* THE BANNER                                                          */
/* ------------------------------------------------------------------ */

/* Sits between the tiles and the toolbar and only exists while a tile
   is active. Says what is being shown and gives one click back. */
export function FilterBanner({ tiles, focus, onClear, clearLabel }) {
  if (!focus) return null;
  const t = (tiles || []).find(function (x) { return x.key === focus; });
  if (!t) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 10,
        padding: '8px 12px',
        borderRadius: 8,
        background: 'var(--g-surface)',
        border: '1px solid var(--g-divider)',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--g-text-2)' }}>
        {t.hint || ('Filtered to ' + t.label)}
      </span>
      <button
        className="btn outline"
        style={{ height: 28, padding: '0 10px', fontSize: 12, marginLeft: 'auto' }}
        onClick={onClear}
      >
        {clearLabel || 'Show all'}
      </button>
    </div>
  );
}
